import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Clock,
  GraduationCap,
  Heart,
  Lightbulb,
  Mail,
  MapPin,
  Phone,
  Users,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Kicker, SmchsLayout, PageHero } from '@/components/smchs/Shell';
import { useAboutContent } from '@/hooks/content-hooks';
import { useCatalogOverview } from '@/hooks/catalog-hooks';
import { bookclubStatsApi } from '@/services/bookclub-api';

const TEAM_TINTS = [
  'from-[#1E2D5C] to-[#0A1226]',
  'from-[#0F3E2E] to-[#041F14]',
  'from-[#7C2D12] to-[#431407]',
  'from-[#164E63] to-[#083344]',
];

const VALUES_ICONS = [Users, Lightbulb, Heart, GraduationCap];

const DEFAULTS: Record<string, string> = {
  'about.hero.kicker_label': 'About the library',
  'about.hero.tagline':
    "The library at St Mary's Catholic High School, Dubai: a place to read, research, talk books, or just find a quiet desk between lessons.",
  'about.mission.paragraph_1':
    'The library is here so that every student and teacher at SMCHS can get the book, article or database they need, and has somewhere to sit and work with it. In practice that means a catalogue you can search from your phone, librarians who will help you track a source down, and longer opening hours when exams come round.',
  'about.mission.paragraph_2':
    "It is also where school life spills over after the bell: IB collections and teachers' reading lists on one side, student-run book clubs and author visits on the other.",
  'about.mission.established': 'Est. 1985',
  'about.mission.location': "St Mary's · Dubai",
  // Titles and book clubs are read from the API below. These two have no
  // public endpoint, so they stay blank until an admin sets them in
  // Site Content -- never a placeholder figure presented as fact.
  'about.stats.active_members': '',
  'about.stats.events_per_year': '',
  'about.values.items': JSON.stringify([
    { title: 'Accessibility', description: 'Open to every student, teacher and visitor. If you are part of SMCHS, the library is yours.' },
    { title: 'Innovation', description: 'A catalogue you can search from your phone, reserve from, and collect from at the desk.' },
    { title: 'Community', description: 'Book clubs run by students, author visits, and a reading challenge every term.' },
    { title: 'Academic excellence', description: "Shelves stocked to the syllabus, including the IB collection and teachers' reading lists." },
  ]),
  'about.team.intro':
    'A small team of librarians. Ask them anything, from where a book is shelved to how to cite it.',
  // Real staff only. Populated by an admin in Site Content; never seeded with
  // invented people, which would put fabricated names and bios on a live page
  // presenting them as the school's team.
  'about.team.members': JSON.stringify([]),
  'about.hours.schedule': JSON.stringify([
    { day: 'Sunday to Thursday', time: '7:30 AM – 4:00 PM' },
    { day: 'Friday', time: '7:30 AM – 12:00 PM' },
    { day: 'Saturday', time: 'Closed' },
    { day: 'Exam period', time: 'Extended hours announced' },
  ]),
  'about.contact.phone': '+971 4 288 6700',
  'about.contact.email': 'library@smchs.ae',
  'about.contact.address': "St Mary's Catholic High School, Muhaisnah 4, Dubai, United Arab Emirates",
  'about.contact.maps_url': "https://maps.google.com/?q=St+Mary's+Catholic+High+School+Dubai",
  'about.cta.description':
    'Sign up with your SMCHS account to borrow books, join clubs, track reading challenges, and access the full catalog.',
};

type ValueItem = { title: string; description: string };
type TeamMember = { name: string; role: string; bio: string };
type HourRow = { day: string; time: string };

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('');
}

function parseJson<T>(raw: string): T[] {
  try { return JSON.parse(raw) as T[]; } catch { return []; }
}

const About: React.FC = () => {
  const { data: content } = useAboutContent();
  const c = (key: string): string => content?.[key] ?? DEFAULTS[key] ?? '';

  // Both endpoints are IsAuthenticatedOrReadOnly, so they are safe to call
  // while signed out. An admin override in Site Content still wins.
  const { data: catalog } = useCatalogOverview();
  const { data: clubStats } = useQuery({
    queryKey: ['about', 'bookclub', 'stats'],
    queryFn: () => bookclubStatsApi.getStats(),
    staleTime: 10 * 60 * 1000,
  });

  const titlesStat =
    content?.['about.stats.titles'] ?? catalog?.counts?.books?.toLocaleString() ?? '—';
  const clubsStat =
    content?.['about.stats.book_clubs'] ?? clubStats?.active_clubs?.toLocaleString() ?? '—';

  const valuesData = parseJson<ValueItem>(c('about.values.items'));
  const teamData = parseJson<TeamMember>(c('about.team.members'));
  const hoursData = parseJson<HourRow>(c('about.hours.schedule'));

  return (
    <SmchsLayout>
      <PageHero
        kickerLabel={c('about.hero.kicker_label')}
        title={
          <>
            Where curiosity finds its{' '}
            <span className="font-serif italic font-normal text-[var(--primary)]">home</span>.
          </>
        }
        tagline={c('about.hero.tagline')}
      />

      {/* ── MISSION ────────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
        <Kicker label="Our mission" />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <h2 className="font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[22ch]">
              A working library{' '}
              since 1985.
            </h2>
            <p className="mt-6 text-[var(--ink-soft)] text-[1.05rem] leading-[1.6] max-w-[56ch]">
              {c('about.mission.paragraph_1')}
            </p>
            <p className="mt-4 text-[var(--ink-soft)] text-[1.05rem] leading-[1.6] max-w-[56ch]">
              {c('about.mission.paragraph_2')}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-7 space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center">
                  <BookOpen className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-display font-bold text-[1.1rem]">{c('about.mission.established')}</div>
                  <div className="text-[12px] text-[var(--muted)] font-mono uppercase tracking-wider">
                    {c('about.mission.location')}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[var(--border)]">
                {[
                  { v: titlesStat, l: 'Titles' },
                  { v: clubsStat, l: 'Book clubs' },
                  { v: c('about.stats.active_members'), l: 'Active members' },
                  { v: c('about.stats.events_per_year'), l: 'Events / year' },
                ].filter((s) => s.v).map((s) => (
                  <div key={s.l}>
                    <div className="font-display font-[900] text-[1.75rem] leading-none tracking-[-0.02em]">
                      {s.v}
                    </div>
                    <div className="text-[11px] text-[var(--muted)] font-mono uppercase tracking-wider mt-1">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ─────────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
        <Kicker label="What we stand for" />
        <h2 className="mt-6 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[20ch]">
          The things we{' '}
          hold to.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {valuesData.map((v, i) => {
            const Icon = VALUES_ICONS[i % VALUES_ICONS.length];
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: 'easeOut' }}
                className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-7 hover:border-[var(--ink)]/30 hover:shadow-[0_20px_40px_-20px_rgba(20,24,43,0.18)] transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="font-display font-bold text-[1.25rem] tracking-tight">{v.title}</div>
                <p className="mt-2 text-[14px] text-[var(--ink-soft)] leading-[1.55]">{v.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ── TEAM ───────────────────────────────────────────────────────── */}
      {/* Hidden entirely until an admin adds real staff -- a heading over an
          empty grid reads as broken, and inventing people to fill it is worse. */}
      {teamData.length > 0 && (
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
        <Kicker label="The team" />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <h2 className="col-span-1 lg:col-span-7 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em]">
            The people behind the{' '}
            shelf.
          </h2>
          <p className="col-span-1 lg:col-span-5 text-[var(--ink-soft)] text-[1rem] leading-[1.55]">
            {c('about.team.intro')}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {teamData.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: 'easeOut' }}
              className="group rounded-2xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden hover:border-[var(--ink)]/30 hover:shadow-[0_20px_40px_-20px_rgba(20,24,43,0.18)] transition-all"
            >
              <div className={`relative h-32 bg-gradient-to-br ${TEAM_TINTS[i % TEAM_TINTS.length]} flex items-center justify-center`}>
                <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(circle_at_25%_15%,white,transparent_60%)]" />
                <span className="relative font-display font-[900] text-[2.5rem] text-white/90 tracking-tight">
                  {initials(m.name)}
                </span>
              </div>
              <div className="p-6">
                <div className="font-display font-bold text-[1.15rem] tracking-tight">{m.name}</div>
                <div className="text-[12px] font-mono uppercase tracking-wider text-[var(--primary)] mt-1">
                  {m.role}
                </div>
                <p className="mt-3 text-[13.5px] text-[var(--ink-soft)] leading-[1.5]">{m.bio}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
      )}

      {/* ── HOURS & CONTACT ────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-10">
        <Kicker label="Visit us" />
        <h2 className="mt-6 font-display font-[900] text-[2.25rem] md:text-[3rem] leading-[1.02] tracking-[-0.025em] max-w-[22ch]">
          Come find your next{' '}
          favourite{' '}
          book.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Hours card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center">
                <Clock className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display font-bold text-[1.15rem]">Library hours</div>
            </div>
            <div className="space-y-3">
              {hoursData.map((h) => (
                <div key={h.day} className="flex items-center justify-between py-2.5 border-b border-[var(--border)] last:border-0">
                  <span className="text-[14px] text-[var(--ink-soft)]">{h.day}</span>
                  <span className="text-[14px] font-medium font-mono">{h.time}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Location card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.08, ease: 'easeOut' }}
            className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center">
                <MapPin className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display font-bold text-[1.15rem]">Location</div>
            </div>
            <div className="space-y-4">
              <p className="text-[14px] text-[var(--ink-soft)] leading-[1.6]" style={{ whiteSpace: 'pre-line' }}>
                {c('about.contact.address')}
              </p>
              <div className="rounded-xl bg-[var(--surface-2)] border border-[var(--border)] h-[140px] flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="w-6 h-6 text-[var(--primary)] mx-auto mb-2" />
                  <span className="text-[12px] font-mono uppercase tracking-wider text-[var(--muted)]">
                    Map embed
                  </span>
                </div>
              </div>
              <a
                href={c('about.contact.maps_url')}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors"
              >
                Get directions <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>

          {/* Contact card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: 0.16, ease: 'easeOut' }}
            className="rounded-2xl border border-[var(--border)] bg-[var(--ink)] text-[var(--paper)] p-7"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[var(--paper)]/10 flex items-center justify-center">
                <Mail className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display font-bold text-[1.15rem]">Get in touch</div>
            </div>
            <div className="space-y-5">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--paper)]/50 mb-1.5">
                  Main desk
                </div>
                <div className="flex items-center gap-2 text-[15px]">
                  <Phone className="w-4 h-4 text-[var(--paper)]/60" />
                  {c('about.contact.phone')}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--paper)]/50 mb-1.5">
                  Email
                </div>
                <div className="flex items-center gap-2 text-[15px]">
                  <Mail className="w-4 h-4 text-[var(--paper)]/60" />
                  {c('about.contact.email')}
                </div>
              </div>
              <div className="pt-4 border-t border-[var(--paper)]/10">
                <a
                  href="/catalog"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[var(--paper)] text-[var(--ink)] px-5 py-3 rounded-xl font-medium text-[14px] hover:bg-[var(--primary)] hover:text-[var(--paper)] transition-colors"
                >
                  Browse the catalog <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CLOSING CTA ────────────────────────────────────────────────── */}
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-24">
        <div className="relative rounded-3xl bg-[var(--ink)] text-[var(--paper)] p-10 md:p-16 overflow-hidden">
          <div className="absolute -right-24 -bottom-24 w-96 h-96 rounded-full border border-[var(--paper)]/10" />
          <div className="absolute -right-12 -bottom-12 w-72 h-72 rounded-full border border-[var(--paper)]/10" />
          <div className="absolute right-6 bottom-6 w-40 h-40 rounded-full bg-[var(--primary)]/20 blur-3xl" />

          <div className="relative max-w-[34ch]">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--paper)]/60 mb-5">
              Join the community
            </div>
            <h2 className="font-display font-[900] text-[2.5rem] md:text-[3.75rem] leading-[0.98] tracking-[-0.03em]">
              Every great reader starts with a{' '}
              library card.
            </h2>
            <p className="mt-6 text-[var(--paper)]/70 max-w-[50ch]">
              {c('about.cta.description')}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/signup"
                className="inline-flex items-center gap-2 bg-[var(--paper)] text-[var(--ink)] px-5 py-3 rounded-xl font-medium text-[14px] hover:bg-[var(--primary)] hover:text-[var(--paper)] transition-colors"
              >
                Get a card <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="/catalog"
                className="inline-flex items-center gap-2 border border-[var(--paper)]/25 hover:border-[var(--paper)]/60 text-[var(--paper)] px-5 py-3 rounded-xl font-medium text-[14px] transition-colors"
              >
                Browse catalog
              </a>
            </div>
          </div>
        </div>
      </section>
    </SmchsLayout>
  );
};

export default About;
